import { useState, lazy, Suspense, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Home, TrendingUp, BookOpen, Mail, Grid3X3 } from 'lucide-react';
import { hapticFeedback } from '@/hooks/useHapticFeedback';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

// Lazy-load BottomSheetDrawer - only loads when menu is opened (~50KB savings)
const BottomSheetDrawer = lazy(() => import('./BottomSheetDrawer'));

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  matchPaths?: string[];
  isDrawerTrigger?: boolean;
}

const navItems: NavItem[] = [
  { 
    path: '/', 
    label: 'Home', 
    icon: Home,
  },
  { 
    path: '/valuation', 
    label: 'Value', 
    icon: TrendingUp,
  },
  { 
    path: '/blog', 
    label: 'Blog', 
    icon: BookOpen,
    matchPaths: ['/blog'],
  },
  { 
    path: '/contact', 
    label: 'Contact', 
    icon: Mail,
  },
  { 
    path: '#menu', 
    label: 'Menu', 
    icon: Grid3X3,
    isDrawerTrigger: true,
  },
];

export const MobileBottomNav = () => {
  const location = useLocation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [pressedItem, setPressedItem] = useState<string | null>(null);
  
  const isActive = useCallback((item: NavItem) => {
    if (item.isDrawerTrigger) return isDrawerOpen;
    if (location.pathname === item.path) return true;
    if (item.matchPaths) {
      return item.matchPaths.some(p => location.pathname.startsWith(p));
    }
    return false;
  }, [location.pathname, isDrawerOpen]);

  const handleNavClick = useCallback((item: NavItem, e: React.MouseEvent) => {
    if (item.isDrawerTrigger) {
      e.preventDefault();
      hapticFeedback('medium');
      setIsDrawerOpen(true);
    } else {
      hapticFeedback('selection');
    }
  }, []);

  const handlePressStart = useCallback((path: string) => {
    setPressedItem(path);
    hapticFeedback('light');
  }, []);

  const handlePressEnd = useCallback(() => {
    setPressedItem(null);
  }, []);

  // Don't show on valuation page (has its own sticky CTA)
  if (location.pathname === '/valuation') {
    return null;
  }

  return (
    <>
      {/* iOS-style frosted glass navigation bar */}
      <nav 
        className={cn(
          "fixed bottom-0 left-0 right-0 z-40 md:hidden",
          "bg-background/80 backdrop-blur-xl backdrop-saturate-150",
          "border-t border-border/30",
          "shadow-[0_-1px_20px_rgba(0,0,0,0.05)]"
        )}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
        role="navigation"
        aria-label="Mobile navigation"
      >
        <div className="flex items-center justify-around h-[60px] px-1">
          {navItems.map((item) => {
            const active = isActive(item);
            const isPressed = pressedItem === item.path;
            const Icon = item.icon;
            
            const content = (
              <motion.div
                className="flex flex-col items-center justify-center relative py-1.5"
                animate={{
                  scale: isPressed ? 0.9 : 1,
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 17 }}
              >
                {/* Active background pill - iOS style */}
                <AnimatePresence>
                  {active && !item.isDrawerTrigger && (
                    <motion.div
                      layoutId="activeNavBg"
                      className="absolute inset-x-1 inset-y-0.5 bg-primary/10 rounded-xl -z-10"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ type: 'spring', stiffness: 350, damping: 25 }}
                    />
                  )}
                </AnimatePresence>
                
                {/* Icon with spring animation */}
                <motion.div
                  animate={{
                    y: active ? -2 : 0,
                  }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                >
                  <Icon className={cn(
                    "h-[22px] w-[22px] transition-colors duration-200",
                    active ? "text-primary" : "text-muted-foreground"
                  )} />
                </motion.div>
                
                {/* Label with smooth fade */}
                <motion.span 
                  className={cn(
                    "text-[10px] font-medium mt-0.5 transition-colors duration-200",
                    active ? "text-primary" : "text-muted-foreground"
                  )}
                  animate={{
                    opacity: active ? 1 : 0.7,
                    fontWeight: active ? 600 : 500,
                  }}
                  transition={{ duration: 0.15 }}
                >
                  {item.label}
                </motion.span>
              </motion.div>
            );

            if (item.isDrawerTrigger) {
              return (
                <button
                  key={item.path}
                  onClick={(e) => handleNavClick(item, e)}
                  onTouchStart={() => handlePressStart(item.path)}
                  onTouchEnd={handlePressEnd}
                  onMouseDown={() => handlePressStart(item.path)}
                  onMouseUp={handlePressEnd}
                  onMouseLeave={handlePressEnd}
                  className={cn(
                    "flex flex-col items-center justify-center flex-1 h-full relative",
                    "rounded-xl mx-0.5 transition-colors duration-150",
                    "active:bg-muted/50"
                  )}
                  aria-label="Open menu"
                  aria-expanded={isDrawerOpen}
                >
                  {content}
                </button>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={(e) => handleNavClick(item, e)}
                onTouchStart={() => handlePressStart(item.path)}
                onTouchEnd={handlePressEnd}
                onMouseDown={() => handlePressStart(item.path)}
                onMouseUp={handlePressEnd}
                onMouseLeave={handlePressEnd}
                className={cn(
                  "flex flex-col items-center justify-center flex-1 h-full relative",
                  "rounded-xl mx-0.5 transition-colors duration-150",
                  "active:bg-muted/50"
                )}
                aria-current={active ? 'page' : undefined}
              >
                {content}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Bottom Sheet Drawer - lazy loaded */}
      <AnimatePresence>
        {isDrawerOpen && (
          <Suspense fallback={null}>
            <BottomSheetDrawer 
              isOpen={isDrawerOpen} 
              onClose={() => setIsDrawerOpen(false)} 
            />
          </Suspense>
        )}
      </AnimatePresence>
    </>
  );
};
